import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {BehaviorSubject, Observable, Subject, throwError} from 'rxjs';
import {catchError, distinctUntilChanged, map, tap, takeUntil} from 'rxjs/operators';

import {environment} from '../../../environments/environment';
import {IUserMin} from '../../editor-interface/shared/models/user-min.model';
import {AUTHORISATION_TASK_REFERRER_ENUM} from '../../shared/constants/diagram-authorisation.constants';
import {
  Q9PKServerErrorResponse,
  TypedHttpErrorResponse
} from '../../shared/models/pk-server-error-response.interface';

@Injectable({
  providedIn: 'root'
})
export class AuthorisationService {
  private currentAuthorisationDiagramId: BehaviorSubject<string> = new BehaviorSubject(undefined);
  private authorisationAlive = new Subject();
  private taskListSubject: BehaviorSubject<IAuthorisationTask[]> = new BehaviorSubject([]);
  private currentTaskSubject: BehaviorSubject<IAuthorisationTask> =
    new BehaviorSubject<IAuthorisationTask>(undefined);
  private taskClosed = new BehaviorSubject(false);
  public authorisationMode$ = this.currentAuthorisationDiagramId.pipe(
    distinctUntilChanged(),
    map(val => !!val)
  );
  public taskList$ = this.taskListSubject.asObservable();
  public currentTask$ = this.currentTaskSubject.asObservable();
  public taskClosed$ = this.taskClosed.asObservable();

  constructor(private http: HttpClient) {}

  public authorisationClosed() {
    return this.authorisationAlive.isStopped;
  }

  public initAuthorisationMode(diagramId, authorisationMode) {
    this.setCurrentDiagramId(diagramId);

    if (authorisationMode === 'true') {
      this.initTaskList();
    } else if (authorisationMode) {
      this.initSingleTaskMeta({taskId: authorisationMode, diagramId});
    }
  }

  private initTaskList() {
    const diagramId = this.currentAuthorisationDiagramId.value;

    if (diagramId) {
      return this.getAuthorisationTaskList(diagramId)
        .pipe(
          tap(taskList => this.updateTaskList(taskList)),
          takeUntil(this.authorisationAlive)
        )
        .subscribe();
    }
  }

  private updateTaskList(taskList) {
    this.taskListSubject.next(taskList);

    if (taskList.length < 1) {
      return this.unsetCurrentId();
    }

    this.setCurrentTaskFromList();
  }

  private initSingleTaskMeta(params) {
    return this.getAuthorisationTaskMeta(params)
      .pipe(
        tap(task => {
          this.taskListSubject.next([task]);
          this.currentTaskSubject.next(task);
          this.taskClosed.next(true);
        }),
        takeUntil(this.authorisationAlive)
      )
      .subscribe();
  }

  private getAuthorisationTaskList(diagramId) {
    return this.http
      .get<IAuthorisationTask[]>(`${environment.API_ENDPOINT}/authorisation/diagram/${diagramId}`)
      .pipe(
        catchError((err: any) => {
          return this.handleServerError(err, diagramId);
        })
      );
  }

  private getAuthorisationTaskMeta({taskId, diagramId}) {
    return this.http
      .get<IAuthorisationTask>(`${environment.API_ENDPOINT}/authorisation/${taskId}/meta`)
      .pipe(
        catchError((err: any) => {
          return this.handleServerError(err, diagramId);
        })
      );
  }

  handleServerError(
    error: TypedHttpErrorResponse<Q9PKServerErrorResponse>,
    diagramId: string = ''
  ): Observable<never> {
    // TODO: add custom handler if needed
    return throwError(() => error);
  }

  wipeAuthorisation() {
    this.unsetCurrentId();
    this.authorisationAlive.complete();
  }

  unsetCurrentId() {
    this.currentAuthorisationDiagramId.next(undefined);
  }

  private setCurrentDiagramId(id) {
    if (!this.authorisationAlive.isStopped) {
      this.currentAuthorisationDiagramId.next(id);
    }
  }

  private setCurrentTaskFromList() {
    const taskList = this.taskListSync();
    const currentDiagramId = this.currentDiagramIdSync();
    const currentTask = taskList.find(t => t.diagram === currentDiagramId);

    this.currentTaskSubject.next(currentTask);
  }

  private currentDiagramIdSync() {
    return this.currentAuthorisationDiagramId.value;
  }

  public taskListSync() {
    return [...this.taskListSubject.value];
  }

  public currentTaskSync() {
    return this.currentTaskSubject.value;
  }

  public rejectTask(payload) {
    const {diagramId, comment, password} = payload;

    return this.http
      .put(`${environment.API_ENDPOINT}/authorisation/diagram/${diagramId}/reject`, {
        comment,
        password
      })
      .pipe(
        catchError((err: any) => {
          return this.handleServerError(err, diagramId);
        })
      );
  }

  public approveTask(payload) {
    const {diagramId, taskStatus: status} = payload;

    return this.http
      .put(`${environment.API_ENDPOINT}/authorisation/diagram/${diagramId}/check`, {status})
      .pipe(
        catchError((err: any) => {
          return this.handleServerError(err, diagramId);
        })
      );
  }

  public signOffTask(payload): Observable<{movedToReady: boolean; task: IAuthorisationTask}> {
    const {diagramId, password} = payload;
    const url =
      `${environment.API_ENDPOINT}/authorisation/diagram/${diagramId}` +
      `/sign-off?referrer=${AUTHORISATION_TASK_REFERRER_ENUM.DE}`;

    return this.http.put<{movedToReady: boolean; task: IAuthorisationTask}>(url, {password}).pipe(
      catchError(error => {
        return this.handleServerError(error);
      })
    );
  }

  public signOffAllTask(payload): Observable<{movedToReady: boolean; task: IAuthorisationTask}> {
    const {password} = payload;
    const url = `${environment.API_ENDPOINT}/authorisation/sign-off`;

    return this.http
      .put<{movedToReady: boolean; task: IAuthorisationTask}>(url, {password})
      .pipe(catchError(error => this.handleServerError(error)));
  }

  public changeCurrentTaskStatus(newTaskStatus) {
    const arr = this.taskListSync();
    const currentDiagramId = this.currentAuthorisationDiagramId.value;
    const taskListWithUpdatedTaskStatus = arr.map(task => {
      if (task.diagram === currentDiagramId) {
        task.status = newTaskStatus;
      }

      return task;
    });

    this.taskListSubject.next(taskListWithUpdatedTaskStatus);
  }

  public getTaskUsers({diagramId, requestId}) {
    return this.http
      .get<IUserHistory>(
        `${environment.API_ENDPOINT}/authorisation/${diagramId}/users/${requestId}`
      )
      .pipe(
        catchError((err: any) => {
          return this.handleServerError(err, diagramId);
        })
      );
  }
}

export interface IAuthorisationTask {
  _id: ObjectId;
  assignedTo: ObjectId;
  connection: IDiagramConnection;
  connectionId: IAuthorisationRequest;
  diagram: ObjectId;
  createdAt: ISODate;
  createdBy: IUserMin;
  description: string;
  requiredBy: ISODate;
  space: ObjectId;
  status: 'notStarted' | 'inProgress' | 'readyToApprove' | 'completed' | 'cancelled';
  title: string;
  type: 'DiagramAuthorisationTask';
  updatedAt: ISODate;
}

interface IDiagramConnection {
  diagramId: ObjectId;
  levelNumber: string;
  map: {
    _id: ObjectId;
    name: string;
  };
  name: string;
  _id: ObjectId;
  state: 'being_authorised';
}

interface IUserHistory {
  total: number;
  list: IHistoryRecord[];
}

export interface ITaskAccessResponse {
  lastApprovedTask: boolean;
  numberOfDiagrams: number;
}

interface IHistoryAuthorisationTask {
  acceptedDateTime: Date | null;
  authorisedDateTime: Date | null;
  cancelledDateTime: Date | null;
  rejectedDateTime: Date | null;
  status: string;
}

interface IHistoryRecord {
  authorisationTask: IHistoryAuthorisationTask;
  user: IUserMin;
}

export interface IAuthorisationRequest {
  _id: ObjectId;
  accepted: number;
  authorised: number;
  completedDate: Date | null;
  completedVersion: number;
  connectionId: ObjectId;
  diagramVersion: ObjectId;
  createdAt: Date;
  entityType: 'diagram';
  map: ObjectId;
  numberOfUsers: number;
  pending: number;
  raisedBy: ObjectId;
  rejected: number;
  requestType: 'authorisation';
  requestedVersion: number;
  requiredBy: Date;
  space: ObjectId;
  status: 'open' | 'closed';
  type: 'DiagramAuthorisationRequest';
  updatedAt: Date;
  __v: number;
  id: ObjectId;
}

// @ts-ignore
type ISODate = string;

// @ts-ignore
type ObjectId = string;
